/**
 * Help Section Configuration
 * Defines the structure and content metadata for the Help page
 */

export interface HelpSubsection {
  id: string
  label: string
}

export interface HelpSection {
  id: string
  label: string
  icon: string
  description: string
  lastUpdated: string
  keywords: string[]
  subsections?: HelpSubsection[]
}

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    icon: 'Lightbulb',
    description: 'Quick introduction to Grackle CRM',
    lastUpdated: '2026-02-13',
    keywords: [
      'overview',
      'introduction',
      'navigation',
      'search',
      'my records',
      'toggle',
      'my',
      'all',
      'sidebar',
      'menu',
    ],
    subsections: [
      { id: 'overview', label: 'Overview' },
      { id: 'navigation', label: 'Navigation' },
      { id: 'search', label: 'Global Search' },
      { id: 'my-view', label: 'My Records View' },
    ],
  },
  {
    id: 'quick-start',
    label: 'Quick Start Checklist',
    icon: 'Rocket',
    description: 'Your first day with Grackle CRM — get set up in minutes',
    lastUpdated: '2026-02-13',
    keywords: [
      'quick start',
      'first day',
      'checklist',
      'setup',
      'onboarding',
      'new user',
      'getting started',
      'profile',
      'daily routine',
    ],
    subsections: [
      { id: 'qs-profile', label: 'Set Up Your Profile' },
      { id: 'qs-dashboard', label: 'Explore Your Dashboard' },
      { id: 'qs-first-records', label: 'Create Your First Records' },
      { id: 'qs-daily-routine', label: 'Your Daily Routine' },
    ],
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    description: 'Your central hub for key metrics and recent activities',
    lastUpdated: '2026-05-30',
    keywords: [
      'metrics',
      'statistics',
      'pipeline',
      'visualization',
      'kpi',
      'performance',
      'revenue',
      'activity feed',
      'calls today',
      'fiscal year',
      'this year',
      'year to date',
      'all data',
      'my data',
      'date range',
    ],
  },
  {
    id: 'activities',
    label: 'Activities',
    icon: 'CheckSquare',
    description: 'Manage tasks, events, emails, and calls',
    lastUpdated: '2026-05-30',
    keywords: [
      'tasks',
      'events',
      'emails',
      'calendar',
      'schedule',
      'meeting',
      'google meet',
      'google calendar',
      'follow up',
      'to-do',
      'default owner',
      'pre-select',
      'call',
      'phone',
      'voip',
      'zoiper',
      'click-to-call',
      'incoming call',
      'disposition',
      'call log',
      'compose',
      'send email',
      'reply',
      'gmail api',
      'threading',
      'due date',
      'reminder',
      'due tomorrow',
      'overdue',
      'tasks on tickets',
      'connect google',
      'gmail sync',
      'sync history',
      'lookback',
    ],
    subsections: [
      { id: 'tasks', label: 'Tasks' },
      { id: 'events', label: 'Events' },
      { id: 'emails', label: 'Emails' },
      { id: 'call-logging', label: 'Call Logging' },
    ],
  },
  {
    id: 'ticketing',
    label: 'Ticketing',
    icon: 'Ticket',
    description: 'Create and manage support tickets',
    lastUpdated: '2026-03-14',
    keywords: [
      'tickets',
      'support',
      'help desk',
      'issues',
      'requests',
      'priority',
      'status',
      'sla',
      'tasks on tickets',
      'multi-select',
      'search by field',
      'ticket number',
      'edit ticket',
      'comment edit',
      'comment delete',
      'reassignment',
      'email notification',
      'escalated',
      'jira',
      'attachments',
      'file upload',
      'internal support',
      'create contact',
      'product',
    ],
    subsections: [
      { id: 'creating-tickets', label: 'Creating Tickets' },
      { id: 'managing-tickets', label: 'Managing Tickets' },
      { id: 'editing-tickets', label: 'Editing Tickets' },
      { id: 'ticket-comments', label: 'Comments' },
      { id: 'ticket-attachments', label: 'Attachments' },
      { id: 'ticket-tasks', label: 'Tasks on Tickets' },
      { id: 'ticket-filtering', label: 'Filtering & Searching' },
      { id: 'internal-support', label: 'Internal Support Requests' },
    ],
  },
  {
    id: 'crm',
    label: 'CRM',
    icon: 'Users',
    description: 'Manage your customer relationships',
    lastUpdated: '2026-05-30',
    keywords: [
      'accounts',
      'contacts',
      'leads',
      'opportunities',
      'customers',
      'companies',
      'deals',
      'sales',
      'duplicates',
      'duplicate',
      'merge',
      'detection',
      'warning',
      'similarity',
      'notes',
      'convert',
      'pipeline',
      'address',
      'autocomplete',
      'google maps',
      'auto-fill',
      'lead score',
      'deal health',
      'account health',
      'win probability',
      'velocity',
      'billing address',
      'contact role',
      'searchable',
      'kanban',
      'kanban view',
      'drag and drop',
      'lead source column',
      'click to edit',
      'inline edit',
    ],
    subsections: [
      { id: 'accounts', label: 'Accounts' },
      { id: 'contacts', label: 'Contacts' },
      { id: 'leads', label: 'Leads' },
      { id: 'opportunities', label: 'Opportunities' },
      { id: 'duplicate-detection', label: 'Duplicate Detection' },
      { id: 'record-merging', label: 'Record Merging' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: 'ShoppingCart',
    description: 'Manage quotes, orders, products, and pricing',
    lastUpdated: '2026-03-06',
    keywords: [
      'quotes',
      'orders',
      'products',
      'pricebooks',
      'pricing',
      'catalog',
      'fulfillment',
      'proposal',
      'line items',
      'address',
      'autocomplete',
      'billing address',
      'pdf',
      'quote pdf',
      'download pdf',
      'view pdf',
      'sales rep',
    ],
    subsections: [
      { id: 'quotes', label: 'Quotes' },
      { id: 'orders', label: 'Orders' },
      { id: 'products', label: 'Products' },
      { id: 'pricebooks', label: 'Price List' },
    ],
  },
  {
    // Separate from 'sales' because the question people arrive with is about the workflow
    // ("whose turn is it", "can I sign this") rather than about quoting.
    id: 'contract-reviews',
    label: 'Contract Reviews',
    icon: 'FileSignature',
    description: 'Contracts going back and forth, and who signs',
    lastUpdated: '2026-10-19',
    keywords: [
      'contract',
      'contracts',
      'contract review',
      'review',
      'redline',
      'redlining',
      'negotiation',
      'rounds',
      'sign',
      'signature',
      'signed',
      'docusign',
      'agreed',
      'declined',
      'withdrawn',
      'awaiting signature',
      'changes proposed',
      'msa',
      'nda',
      'terms',
      'legal',
      'draft',
      'whose turn',
    ],
    subsections: [
      { id: 'cr-raising', label: 'Raising One' },
      { id: 'cr-rounds', label: 'Going Back and Forth' },
      { id: 'cr-signing', label: 'Agreeing and Signing' },
      { id: 'cr-visibility', label: 'Who Can See a Review' },
    ],
  },
  {
    id: 'sales-workflows',
    label: 'Sales Workflows',
    icon: 'Route',
    description: 'Follow the lead-to-close lifecycle step by step',
    lastUpdated: '2026-03-05',
    keywords: [
      'workflow',
      'lead to close',
      'lifecycle',
      'pipeline',
      'conversion',
      'process',
      'stages',
      'qualify',
      'closing',
      'handoff',
      'renewal',
      'renewal opportunity',
      'contract end date',
    ],
    subsections: [
      { id: 'sw-lead-capture', label: 'Capturing Leads' },
      { id: 'sw-qualification', label: 'Qualifying Leads' },
      { id: 'sw-conversion', label: 'Converting to Opportunities' },
      { id: 'sw-pipeline', label: 'Working the Pipeline' },
      { id: 'sw-closing', label: 'Closing the Deal' },
      { id: 'sw-post-close', label: 'Post-Close Follow-Up' },
      { id: 'sw-renewals', label: 'Renewal Opportunities' },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics & Reports',
    icon: 'BarChart3',
    description: 'Gain insights from your data',
    lastUpdated: '2026-05-30',
    keywords: [
      'reports',
      'analytics',
      'charts',
      'graphs',
      'pipeline',
      'forecast',
      'win loss',
      'report builder',
      'custom reports',
      'revenue',
      'lead source',
      'days to close',
      'funnel',
      'date range',
      'filter',
      'deal health',
      'health score',
      'at risk',
      'predictive',
      'lead score',
      'lead scoring',
      'engagement',
      'profile completeness',
      'velocity',
      'stage velocity',
      'win probability',
      'account health',
      'next best action',
      'renewal risk',
      'momentum',
      'slippage',
      'fiscal year',
      'year to date',
      'ytd',
      'this year',
      'date range',
    ],
    subsections: [
      { id: 'analytics-overview', label: 'Analytics Dashboard' },
      { id: 'reports', label: 'Pre-Built Reports' },
      { id: 'report-builder', label: 'Custom Report Builder' },
      { id: 'deal-health', label: 'Deal Health Score' },
      { id: 'lead-scoring', label: 'Lead Scoring' },
      { id: 'stage-velocity', label: 'Stage Velocity Tracker' },
      { id: 'win-probability', label: 'Win Probability' },
      { id: 'account-health', label: 'Account Health Score' },
      { id: 'next-best-action', label: 'Next Best Action' },
    ],
  },
  {
    // Invoiced actuals from QuickBooks, which is a different question from the
    // opportunity pipeline covered by 'analytics'. Keywords lean heavily on the words
    // people actually type when a number looks wrong ("unattributed", "house account",
    // "my sales"), because the search index is label + description + keywords only.
    id: 'sales-trackers',
    label: 'Sales Trackers',
    icon: 'LineChart',
    description: 'What has actually been invoiced, by rep and by product, against target',
    lastUpdated: '2026-09-30',
    keywords: [
      'sales tracker',
      'sales trackers',
      'billings',
      'invoiced',
      'actuals',
      'quickbooks',
      'upload',
      'monthly upload',
      'import',
      'targets',
      'target',
      'spread',
      'annual target',
      'forecast',
      'weekly forecast',
      'monday meeting',
      'transactions',
      'my sales',
      'my transactions',
      'by rep',
      'by product',
      'rep',
      'reps',
      'commission',
      'attribution',
      'attribute',
      'reattribute',
      'unattributed',
      'house accounts',
      'alias',
      'products and reps',
      'software',
      'service',
      'remediation',
      'product category',
      'cad',
      'exchange rate',
      'australian',
      'fiscal year',
      'quarter',
      'month',
      'top clients',
    ],
    subsections: [
      { id: 'st-overview', label: 'Overview and Periods' },
      { id: 'st-my-sales', label: 'Your Own Sales' },
      { id: 'st-breakdowns', label: 'By Product and By Rep' },
      { id: 'st-transactions', label: 'Transactions' },
      { id: 'st-targets', label: 'Targets' },
      { id: 'st-forecast', label: 'Weekly Forecast' },
      { id: 'st-upload', label: 'Getting the Data In' },
      { id: 'st-attribution', label: 'Products & Reps' },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    icon: 'FolderOpen',
    description: 'Document management and file storage',
    lastUpdated: '2026-02-13',
    keywords: [
      'documents',
      'files',
      'attachments',
      'storage',
      'upload',
      'download',
      'proposals',
      'contracts',
    ],
  },
  {
    id: 'pipedrive',
    label: 'Pipedrive Integration',
    icon: 'TrendingUp',
    description: 'Access your historical Pipedrive data',
    lastUpdated: '2026-02-13',
    keywords: [
      'pipedrive',
      'integration',
      'organizations',
      'people',
      'deals',
      'import',
      'sync',
      'migration',
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: 'Settings',
    description: 'System settings and user management',
    lastUpdated: '2026-05-30',
    keywords: [
      'users',
      'roles',
      'permissions',
      'admin',
      'super admin',
      'impersonation',
      'settings',
      'enhancement tracker',
      'export',
      'csv',
      'zip',
      'google',
      'dark mode',
      'marketing',
      'orders',
      'quotes',
      'sales',
      'user types',
      'mailchimp',
      'audience',
      'sync contacts',
      'api key',
      'notifications',
      'bell icon',
      'real-time',
      'picklist',
      'picklists',
      'renewal',
      'renewal templates',
      'renewal tasks',
      'google chat',
      'webhook',
      'tabs',
      'dropdown',
      'stage',
      'priority',
      'google integration',
      'connect google account',
      'connect google',
      'gmail sync',
      'calendar sync',
      'manual',
      'per-user',
      'oauth',
      'oauth consent',
      'disconnect',
      'reconnect',
      'opt out',
      'sync preferences',
      'server-side export',
      'export all records',
    ],
    subsections: [
      { id: 'users', label: 'User Management' },
      { id: 'exporting', label: 'Exporting Data' },
      { id: 'impersonation', label: 'User Impersonation' },
      { id: 'settings', label: 'Settings' },
      { id: 'google-integration', label: 'Google Integration' },
      { id: 'enhancement-tracker', label: 'Enhancement Tracker' },
      { id: 'picklist-management', label: 'Picklist Management' },
      { id: 'renewal-templates', label: 'Renewal Templates' },
      { id: 'google-chat-settings', label: 'Google Chat' },
    ],
  },
  {
    id: 'keyboard-shortcuts',
    label: 'Keyboard Shortcuts',
    icon: 'Keyboard',
    description: 'Navigate faster with keyboard commands',
    lastUpdated: '2026-02-13',
    keywords: [
      'keyboard',
      'shortcuts',
      'hotkeys',
      'ctrl',
      'tab',
      'enter',
      'escape',
      'accessibility',
    ],
  },
  {
    id: 'common-tasks',
    label: 'Common Tasks',
    icon: 'BookOpen',
    description: 'Quick guides for frequent operations',
    lastUpdated: '2026-02-13',
    keywords: [
      'create',
      'edit',
      'delete',
      'filter',
      'search',
      'bulk',
      'export',
      'how to',
      'step by step',
      'recent searches',
      'recently searched',
    ],
  },
  {
    id: 'tips',
    label: 'Tips & Best Practices',
    icon: 'Star',
    description: 'Work smarter with proven strategies for high-volume sales',
    lastUpdated: '2026-02-13',
    keywords: [
      'tips',
      'best practices',
      'productivity',
      'efficiency',
      'high volume',
      'speed',
      'data quality',
      'pipeline management',
      'collaboration',
    ],
    subsections: [
      { id: 'tips-speed', label: 'Speed Tips' },
      { id: 'tips-data-quality', label: 'Keeping Data Clean' },
      { id: 'tips-pipeline', label: 'Pipeline Management' },
      { id: 'tips-collaboration', label: 'Team Collaboration' },
    ],
  },
  {
    id: 'faq',
    label: 'FAQ & Troubleshooting',
    icon: 'MessageCircleQuestion',
    description: 'Answers to common questions and solutions to frequent issues',
    lastUpdated: '2026-02-13',
    keywords: [
      'faq',
      'frequently asked',
      'troubleshoot',
      'problem',
      'issue',
      'error',
      'question',
      'fix',
      'not working',
      'missing',
    ],
    subsections: [
      { id: 'faq-general', label: 'General Questions' },
      { id: 'faq-troubleshooting', label: 'Troubleshooting' },
    ],
  },
  {
    id: 'whats-new',
    label: "What's New",
    icon: 'Sparkles',
    description: 'Recent features and improvements',
    lastUpdated: '2026-09-30',
    keywords: [
      'new',
      'recent',
      'update',
      'release',
      'feature',
      'changelog',
      'improvement',
      'address autocomplete',
      'google maps',
      'default owner',
      'enhanced analytics',
      'closed won',
      'auto order',
      'live quote',
      'synced quote',
      'user types',
      'resizable',
      'sortable',
      'stage velocity',
      'win probability',
      'account health',
      'next best action',
      'predictive analytics',
      'call logging',
      'click-to-call',
      'voip',
      'zoiper',
      'email compose',
      'mailchimp',
      'notifications',
      'quote pdf',
      'dark mode toggle',
      'picklist',
      'renewal',
      'google chat',
      'settings tabs',
      'tabbed',
      'tasks on tickets',
      'task reminders',
      'due date reminders',
      'related-to',
      'billing address',
      'renewal opportunity',
      'recent searches',
      'clickable search',
      'internal support',
      'jira',
      'ticket attachments',
      'file upload',
      'ticket edit',
      'comment edit',
      'sales rep pdf',
      'escalated',
      'reassignment email',
      'google integration',
      'google sync opt out',
      'kanban',
      'opportunities kanban',
      'fiscal year',
      'year to date',
      'lead source column',
      'click to edit',
    ],
  },
  {
    id: 'support',
    label: 'Need Additional Help?',
    icon: 'AlertCircle',
    description: 'Get support and report issues',
    lastUpdated: '2026-03-14',
    keywords: [
      'support',
      'help',
      'accessibility',
      'wcag',
      'contact',
      'administrator',
      'feedback',
      'internal support',
      'request form',
    ],
  },
]

/**
 * Get all section IDs including subsections
 */
export function getAllSectionIds(): string[] {
  const ids: string[] = []
  HELP_SECTIONS.forEach((section) => {
    ids.push(section.id)
    section.subsections?.forEach((sub) => ids.push(sub.id))
  })
  return ids
}

export interface HelpSearchResult {
  /** The parent section that matched (or contains the matching subsection). */
  section: HelpSection
  /** Matching subsections, when the query hit a subsection label. */
  matchedSubsections: HelpSubsection[]
}

/**
 * Look up a single section by id.
 */
export function getHelpSection(id: string): HelpSection | undefined {
  return HELP_SECTIONS.find((s) => s.id === id)
}

/**
 * Rich search that returns matching sections along with any subsections whose
 * label matched, so result cards can surface specific topics. Returns all
 * sections (no subsection emphasis) for an empty query.
 */
export function searchHelpResults(query: string): HelpSearchResult[] {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) {
    return HELP_SECTIONS.map((section) => ({ section, matchedSubsections: [] }))
  }

  const results: HelpSearchResult[] = []
  HELP_SECTIONS.forEach((section) => {
    const matchedSubsections =
      section.subsections?.filter((sub) => sub.label.toLowerCase().includes(trimmed)) ?? []

    const sectionMatches =
      section.label.toLowerCase().includes(trimmed) ||
      section.description.toLowerCase().includes(trimmed) ||
      section.keywords.some((kw) => kw.toLowerCase().includes(trimmed)) ||
      matchedSubsections.length > 0

    if (sectionMatches) {
      results.push({ section, matchedSubsections })
    }
  })
  return results
}

/**
 * Search sections by query
 */
export function searchHelpSections(query: string): string[] {
  if (!query.trim()) return HELP_SECTIONS.map((s) => s.id)

  const lowerQuery = query.toLowerCase()
  return HELP_SECTIONS.filter((section) => {
    // Check section label and description
    if (section.label.toLowerCase().includes(lowerQuery)) return true
    if (section.description.toLowerCase().includes(lowerQuery)) return true

    // Check keywords
    if (section.keywords.some((kw) => kw.toLowerCase().includes(lowerQuery))) return true

    // Check subsection labels
    if (section.subsections?.some((sub) => sub.label.toLowerCase().includes(lowerQuery)))
      return true

    return false
  }).map((s) => s.id)
}
